import { extractInvoice, Invoice, InvoiceQueryData } from './invoice'

type PromotionType = 'free_trial' | 'generic'

/*****************
 * Subscription  *
*****************/

type BaseSubscription = {
  id: string
  billingClosed: boolean
  active: boolean
}

export interface Subscription extends BaseSubscription {
  startAt: Date
  endAt?: Date
  planCost: PlanCost
  promotion: SubscriptionPromotion | null
  pricing: Pricing
}

export interface SubscriptionQueryData extends BaseSubscription {
  startAt: string
  endAt: string | null
  planCost: PlanCostQueryData
  promotion: SubscriptionPromotionQueryData | null
  pricing: PricingQueryData
}

export const extractSubscription = (s: SubscriptionQueryData): Subscription => {
  return {
    ...s,
    startAt: new Date(s.startAt),
    endAt: s.endAt ? new Date(s.endAt) : undefined,
    planCost: extractPlanCost(s.planCost),
    promotion: s.promotion ? extractSubscriptionPromotion(s.promotion) : null,
    pricing: extractPricing(s.pricing)
  }
}

/*****************
 * Plan Cost  *
*****************/
export type BillingCycle = 'MONTHLY' | 'YEARLY'

type BasePlanCost = {
  id: string
  billingCycle: BillingCycle
  taxRate: number
}

export interface PlanCost extends BasePlanCost {
  plan?: Plan
  pricing: Pricing
}

export interface PlanCostQueryData extends BasePlanCost {
  plan?: PlanQueryData
  pricing: PricingQueryData
}

export const extractPlanCost = (pc: PlanCostQueryData): PlanCost => {
  return {
    ...pc,
    plan: pc.plan ? extractPlan(pc.plan) : undefined,
    pricing: extractPricing(pc.pricing)
  }
}

/*****************
 * Plan  *
*****************/

type BasePlan = {
  id: string
  name: string
  description: string
  code: string
  active: boolean
  autoRenew: boolean
  features?: PlanFeature[]
  isFreeTrialAvailable: boolean
}

export interface Plan extends BasePlan {
  promotion: PlanPromotion | null
  costs?: PlanCost[]
}

export interface PlanQueryData extends BasePlan {
  promotion: PlanPromotionQueryData | null
  costs?: PlanCostQueryData[]
}

type PlanFeature = {
  code: string
  enabled: boolean
  description: string
}

export const extractPlan = (p: PlanQueryData): Plan => {
  return {
    ...p,
    promotion: p.promotion ? extractPlanPromotion(p.promotion) : null,
    costs: p.costs ? p.costs.map(extractPlanCost) : undefined,
  }
}

/*******************
 * Plan Promotion  *
********************/

type BasePlanPromotion = {
  id: string
  code: string
  name: string
  promotionType: PromotionType
  description: string
  duration: number
}

export interface PlanPromotion extends BasePlanPromotion {
  discount: number
  startAt: Date
  endAt?: Date
}

interface PlanPromotionQueryData extends BasePlanPromotion {
  discount: string
  startAt: string
  endAt: string | null
}

const extractPlanPromotion = (p: PlanPromotionQueryData): PlanPromotion => {
  return {
    ...p,
    discount: parseFloat(p.discount),
    startAt: new Date(p.startAt),
    endAt: p.endAt ? new Date(p.endAt) : undefined
  }
}

/**************************
 * Subscription Promotion *
***************************/

type BaseSubscriptionPromotion = {
  active: boolean
  name: string
  description: string
  promotionType: PromotionType

}

export interface SubscriptionPromotion extends BaseSubscriptionPromotion {
  discount: number
  endAt?: Date
}

interface SubscriptionPromotionQueryData extends BaseSubscriptionPromotion {
  discount: string
  endAt: string | null
}

const extractSubscriptionPromotion = (
  sp: SubscriptionPromotionQueryData
): SubscriptionPromotion => {
  return {
    ...sp,
    discount: parseFloat(sp.discount),
    endAt: sp.endAt ? new Date(sp.endAt) : undefined
  }
}

/************
 * Pricing  *
*************/

type BasePricing = {
  currency: string
}

export interface Pricing extends BasePricing {
  net: number
  tax: number
  taxRate?: number
  gross: number
}

interface PricingQueryData extends BasePricing {
  net: string
  tax: string
  taxRate?: string
  gross: string
}

const extractPricing = (p: PricingQueryData): Pricing => {
  return {
    ...p,
    net: parseFloat(p.net),
    tax: parseFloat(p.tax),
    taxRate: p.taxRate ? parseFloat(p.taxRate) : undefined,
    gross: parseFloat(p.gross),
  }
}

/************************
 * Subscription Charge  *
*************************/

type BaseSubscriptionCharge = {
  id: string
}

export interface SubscriptionChargeQueryData extends BaseSubscriptionCharge {
  lastChargeAttempt?: string
  billingPeriods: BillingPeriodInlineQueryData[]
  invoice?: InvoiceQueryData
  pricing: PricingQueryData
}

export interface SubscriptionCharge extends BaseSubscriptionCharge {
  lastChargeAttempt?: Date
  billingPeriods: BillingPeriodInline[]
  invoice?: Invoice
  pricing: Pricing
}

export const extractSubscriptionCharge = (c: SubscriptionChargeQueryData): SubscriptionCharge => {
  return {
    ...c,
    lastChargeAttempt: c.lastChargeAttempt ? new Date(c.lastChargeAttempt) : undefined,
    billingPeriods: c.billingPeriods.map(extractBillingPeriod),
    invoice: c.invoice ? extractInvoice(c.invoice) : undefined,
    pricing: extractPricing(c.pricing),
  }
}

/************************
 * Billing Period       *
*************************/
type BaseBillingPeriodInline = {
  currency: string
  taxRate: number
}

interface BillingPeriodInlineQueryData extends BaseBillingPeriodInline {
  startAt: string
  endAt: string
  price: string
  subscription: SubscriptionQueryData
}

interface BillingPeriodInline extends BaseBillingPeriodInline {
  startAt: Date
  endAt: Date
  price: number
  subscription: Subscription
}

const extractBillingPeriod = (bp: BillingPeriodInlineQueryData): BillingPeriodInline => {
  return {
    ...bp,
    startAt: new Date(bp.startAt),
    endAt: new Date(bp.endAt),
    price: parseFloat(bp.price),
    subscription: extractSubscription(bp.subscription)
  }
}

/*****************
 * PaymentMethod *
 * **************/

export type PaymentMethod = {
  brand: string
  last4: string
  expMonth: number
  expYear: number
  canRemove: boolean
  isExpired: boolean
}

/******************
 * BillingAccount *
 * ***************/

type BaseBillingAccount = {
  firstName: string
  lastName: string
  email: string
  isSuspended: boolean
  suspendedReason: string
  isFreeTrialEligible: boolean
}

export type BillingAccountData = BaseBillingAccount & {
  suspendedAt: string | null
  unsuspendedAt: string | null
  dueAmount: PricingQueryData | null
}

export type BillingAccount = BaseBillingAccount & {
  suspendedAt?: Date | null
  unsuspendedAt?: Date | null
  dueAmount: Pricing | null
}

export const extractBillingAccount = (ba: BillingAccountData): BillingAccount => {
  return {
    ...ba,
    suspendedAt: ba.suspendedAt ? new Date(ba.suspendedAt) : null,
    unsuspendedAt: ba.unsuspendedAt ? new Date(ba.unsuspendedAt) : null,
    dueAmount: ba.dueAmount ? extractPricing(ba.dueAmount) : null
  }
}
